import { useSearchParams } from 'react-router-dom'

import { Campus, Category, useChurchDataProvider } from '@data/ChurchDataProvider'

// Sanctuary files a service or series without a category as the campus's
// uncategorized ones, and lists them when no category is asked for.
const NO_CATEGORY = '';

// The campus and category a Services tab is showing. They live in the URL, so
// a reload or a shared link comes back to the same list. With only one campus
// there's nothing to choose, so it's chosen.
//
// `isReady` is false until the church's campuses have loaded; before then an
// empty list means "not here yet", not "none".
export function useLibraryScope() {
  const { church, campuses, categories } = useChurchDataProvider();
  const isReady = church !== undefined;
  const [searchParams, setSearchParams] = useSearchParams();

  const requestedCampusID = searchParams.get('campus');
  const requestedCategoryID = searchParams.get('category') ?? NO_CATEGORY;

  // A campus or category that's since been deleted reads as unchosen rather
  // than asking the API for something that isn't there.
  // A lone campus is used straight away, rather than chosen by an effect
  // after the first render, which would flash "Choose a Campus" first.
  const campus = campuses.find((candidate) => candidate.id === requestedCampusID) ??
    (campuses.length === 1 ? campuses[0] : null);
  const category = categories.find((candidate) => candidate.id === requestedCategoryID) ?? null;

  const select = (campusID: string, categoryID: string) => {
    const params = new URLSearchParams();

    if (campusID) params.set('campus', campusID);
    if (categoryID) params.set('category', categoryID);

    setSearchParams(params, { replace: true });
  };

  return { campus, category, hasCampuses: campuses.length > 0, isReady, select } as const;
}

// Search, Campus and Category in one row, search taking the largest share.
// Styled by ServiceList, through .services-page.
export default function LibraryFilters(props: {
  campus: Campus | null;
  category: Category | null;
  select: (campusID: string, categoryID: string) => void;
  query: string;
  onQueryChange: (query: string) => void;
  searchPlaceholder: string;
}) {
  const { campuses, categories } = useChurchDataProvider();

  const sortedCampuses = [...campuses].sort((one, two) => one.attributes.name.localeCompare(two.attributes.name));
  const sortedCategories = [...categories].sort((one, two) => one.name.localeCompare(two.name));

  return <div className='services-filters'>
    <label className='services-filter search'>
      <input
        type='search'
        aria-label='Search'
        value={props.query}
        placeholder={props.searchPlaceholder}
        disabled={!props.campus}
        onChange={(e) => props.onQueryChange(e.target.value)}
      />
    </label>

    <label className='services-filter'>
      <select
        aria-label='Campus'
        value={props.campus?.id ?? ''}
        onChange={(e) => props.select(e.target.value, props.category?.id ?? NO_CATEGORY)}>
        <option value='' disabled>Choose a campus</option>
        {sortedCampuses.map((option) => (
          <option key={option.id} value={option.id}>{option.attributes.name}</option>
        ))}
      </select>
    </label>

    <label className='services-filter'>
      <select
        aria-label='Category'
        value={props.category?.id ?? NO_CATEGORY}
        disabled={!props.campus}
        onChange={(e) => props.select(props.campus?.id ?? '', e.target.value)}>
        <option value={NO_CATEGORY}>No Category</option>
        {sortedCategories.map((option) => (
          <option key={option.id} value={option.id}>{option.name}</option>
        ))}
      </select>
    </label>
  </div>
}
